import { computed, ref, useId, useTemplateRef, watch, type Ref, type WritableComputedRef } from 'vue'
import type { OrganizationOption } from '@/runtime/source-gateway/fetchSnapshots'

/**
 * 组织下拉：公司多了原生 <select> 只能滚动找，改成可输入过滤（子串匹配，如输入"华"过滤出所有带"华"的组织）。
 * ConnectDataLoginPanel.vue / ProjectConnectPanel.vue 共用同一套交互——两处原先各自贴一份几乎相同
 * 的状态/处理函数，任何键盘导航或过滤规则的修复都要改两遍，容易漏改一处导致行为分叉；收敛到这里。
 *
 * orgQuery 是输入框里显示的文字，跟 organizationId 分开维护——未打开下拉时它跟着当前选中项的名称走，
 * 打开后用户输入什么就是什么，选中或失焦时再回填。
 * orgHasTyped 区分"刚打开、还没输入"与"已经在输入"两种状态：刚打开时不按 orgQuery（此时还是上次选中项
 * 的完整名称）过滤，否则已选中某组织后再点开会立刻被自己的名字过滤成只剩一条，等于看不到其他选项。
 */
export function useOrgCombobox(organizationId: WritableComputedRef<string>, options: Ref<OrganizationOption[]>) {
  const orgQuery = ref('')
  const orgDropdownOpen = ref(false)
  const orgHighlight = ref(-1)
  const orgHasTyped = ref(false)
  // 用 useTemplateRef 而不是本地 ref()：模板里的 ref="orgInput" 靠这个具名注册跟这里的变量对上，
  // 不依赖"变量名等于模板 ref 字符串"这种只有声明在同一文件里才生效的隐式匹配
  // （挪进composable 后曾经导致 vue-tsc 把它误判成未使用的本地变量）。
  const orgInputRef = useTemplateRef<HTMLInputElement>('orgInput')
  const listboxId = useId()

  const filteredOrgOptions = computed(() => {
    if (!orgHasTyped.value) return options.value
    const q = orgQuery.value.trim().toLowerCase()
    if (!q) return options.value
    return options.value.filter((o) => o.label.toLowerCase().includes(q))
  })

  function optionId(id: string): string {
    return `${listboxId}-opt-${id}`
  }
  /** aria-activedescendant：当前键盘高亮项的 DOM id，供输入框声明"我控制的列表里现在选中了谁" */
  const activeDescendantId = computed(() => {
    const o = filteredOrgOptions.value[orgHighlight.value]
    return o ? optionId(o.organizationId) : undefined
  })

  function syncOrgQueryFromSelection() {
    const found = options.value.find((o) => o.organizationId === organizationId.value)
    orgQuery.value = found ? found.label : ''
  }
  watch(organizationId, () => { if (!orgDropdownOpen.value) syncOrgQueryFromSelection() })
  watch(options, () => { if (!orgDropdownOpen.value) syncOrgQueryFromSelection() })

  function openOrgDropdown() {
    orgDropdownOpen.value = true
    orgHighlight.value = -1
    orgHasTyped.value = false
    requestAnimationFrame(() => orgInputRef.value?.select())
  }
  function onOrgInput() {
    orgDropdownOpen.value = true
    orgHasTyped.value = true
    orgHighlight.value = filteredOrgOptions.value.length ? 0 : -1
  }
  function pickOrg(o: OrganizationOption) {
    organizationId.value = o.organizationId
    orgQuery.value = o.label
    orgDropdownOpen.value = false
  }
  function clearOrg() {
    organizationId.value = ''
    orgQuery.value = ''
    orgHasTyped.value = false
    orgDropdownOpen.value = true
    orgInputRef.value?.focus()
  }
  function onOrgBlur() {
    orgDropdownOpen.value = false
    syncOrgQueryFromSelection()
  }
  function onOrgKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!orgDropdownOpen.value) { openOrgDropdown(); return }
      orgHighlight.value = Math.min(orgHighlight.value + 1, filteredOrgOptions.value.length - 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      // 还没高亮任何一项时（刚打开、未动过方向键）ArrowUp 不该凭空跳到第一项，保持"未选中"
      if (orgHighlight.value < 0) return
      orgHighlight.value = Math.max(orgHighlight.value - 1, 0)
    } else if (e.key === 'Enter') {
      if (orgDropdownOpen.value) {
        e.preventDefault()
        const o = filteredOrgOptions.value[orgHighlight.value]
        if (o) pickOrg(o)
      }
    } else if (e.key === 'Escape') {
      orgDropdownOpen.value = false
      syncOrgQueryFromSelection()
      orgInputRef.value?.blur()
    }
  }

  return {
    orgQuery,
    orgDropdownOpen,
    orgHighlight,
    orgInputRef,
    filteredOrgOptions,
    listboxId,
    optionId,
    activeDescendantId,
    openOrgDropdown,
    onOrgInput,
    pickOrg,
    clearOrg,
    onOrgBlur,
    onOrgKeydown,
  }
}
